import { Select } from 'components/Form/Select';
import { useCallClientNumbers } from 'hooks/fetch/useCallClientNumbers';
import { useContacts } from 'hooks/useContacts';
import { useEffect } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { api } from 'services/api';
import { Button } from 'styles/Button';
import { Form } from 'styles/Form';
import { InputGroup } from 'styles/InputGroup';
import { ModalTitle } from 'styles/Modal';
import { WhatsAppNumber } from 'types/WhatsAppNumber';
import { handleRequestError, handleRequestSuccess } from 'utils/Request';
import closeImg from '../../assets/close.svg';
import { ChangeSessionModalProps } from './types';

function numberLabel(number: WhatsAppNumber) {
  return number.phone_number ? `${number.label} (${number.phone_number})` : number.label;
}

export function ChangeSessionModal({ isOpen, onRequestClose, contact }: ChangeSessionModalProps) {
  const { data: numbers } = useCallClientNumbers();
  const { selectContact } = useContacts();

  const form = useForm<FieldValues>();
  const { handleSubmit, setValue, control, reset, formState } = form;

  useEffect(() => {
    if (!isOpen || !numbers || numbers.length === 0) return;

    const current = numbers.find(number => number.number_id === contact.session);
    if (current) setValue('number_id', current.number_id);
  }, [isOpen, numbers, contact, setValue]);

  function handleRequestCloseModal() {
    reset();
    onRequestClose();
  }

  async function handleChangeSession({ number_id }: { number_id: string }) {
    try {
      const { data } = await api.post(`/contacts/${contact.id}/change-session`, {
        session: number_id,
      });
      selectContact(data);
      handleRequestSuccess('Número de saída alterado com sucesso!');
      handleRequestCloseModal();
    } catch (error) {
      handleRequestError(error, 'Erro ao alterar o número de saída. Por favor, tente novamente!');
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleRequestCloseModal}
      overlayClassName="react-modal-overlay"
      className="react-modal-content"
    >
      <button
        type="button"
        onClick={handleRequestCloseModal}
        className="react-modal-close"
      >
        <img src={closeImg} alt="Fechar modal" loading="lazy" />
      </button>

      <div>
        <ModalTitle>Alterar número de saída</ModalTitle>

        <Form onSubmit={handleSubmit(handleChangeSession as SubmitHandler<FieldValues>)}>
          <FormProvider {...form}>
            <InputGroup>
              <Select
                control={control}
                name="number_id"
                id="number_id"
                label="Número de saída"
                placeholder="Selecionar número de saída"
                fullwidth
                config={{
                  noOptionsMessage: () => 'Nenhum número disponível.',
                  options: numbers ? numbers.map(number => ({ value: number.number_id, label: numberLabel(number) })) : [],
                  closeMenuOnSelect: true,
                }}
              />
            </InputGroup>
            <InputGroup>
              <Button type="submit" fullwidth disabled={formState.isSubmitting}>
                {formState.isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </InputGroup>
          </FormProvider>
        </Form>
      </div>
    </Modal>
  );
}
